<template>
  <label :class="['ui-switch', { 'is-disabled': disabled }]">
    <input
      v-model="model"
      class="ui-switch__input"
      type="checkbox"
      role="switch"
      :disabled="disabled"
      :aria-checked="model ? 'true' : 'false'"
      v-bind="$attrs"
    />
    <span class="ui-switch__track" aria-hidden="true" />
    <span v-if="$slots['default']" class="ui-switch__label"><slot /></span>
  </label>
</template>

<script setup lang="ts">
/**
 * Something on or off that takes effect at once: outline the blocks of
 * text, show the margins. Quantum has none; this one is drawn in its terms,
 * a sunken track with a white knob that fills blue when on.
 *
 * A checkbox underneath, with the switch role, so it is reached with Tab,
 * flipped with Space and announced as a switch. Its label is the slot, and
 * clicking the label flips it too.
 */
defineOptions({ inheritAttrs: false })

withDefaults(defineProps<{ disabled?: boolean }>(), { disabled: false })

const model = defineModel<boolean>({ default: false })
</script>

<style scoped>
.ui-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text);
  cursor: pointer;
  user-select: none;
}

.ui-switch.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ui-switch__input {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  opacity: 0;
}

.ui-switch__track {
  position: relative;
  flex: none;
  width: 32px;
  height: 18px;
  /* Off is a mid grey, so the white knob stands out on it. */
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  background: color-mix(in srgb, var(--color-border-strong) 45%, var(--color-border));
  transition:
    background-color var(--transition-base) ease,
    border-color var(--transition-base) ease;
}

.ui-switch__track::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: var(--shadow-1);
  transition: transform var(--transition-base) var(--ease-standard);
}

.ui-switch__input:checked + .ui-switch__track {
  border-color: var(--color-primary);
  background: var(--color-primary);
}

.ui-switch__input:checked + .ui-switch__track::after {
  transform: translateX(14px);
}

.ui-switch__input:focus-visible + .ui-switch__track {
  box-shadow: var(--shadow-focus);
}

@media (prefers-reduced-motion: reduce) {
  .ui-switch__track,
  .ui-switch__track::after {
    transition: none;
  }
}
</style>
